import React, { useState, useEffect, useMemo } from "react";
import { useFetchModelCapabilityMaps, useFetchIndustries, useDeleteModelCapabilityMap, useToggleCapabilityMapVisibility } from "../hooks/useCapabilityMaps";
import FilterButtonCapabilityMaps from "../partials/capabilityMaps/FilterButtonCapabilityMaps";
import CapabilityMapsCard from "../partials/capabilityMaps/CapabilityMapsCard";
import AddEditCapabilityMapModal from "../partials/capabilityMaps/AddEditCapabilityMapModal";
import DeleteConfirmationModal from "../partials/capabilityMaps/DeleteConfirmationModal";
import ViewCapabilityMapModal from "../partials/capabilityMaps/ViewCapabilityMapModal";
const industryColors = {
  'agnostic': '#94A3B8',
  'government': '#64748B',
  'non-profit / social services': '#10B981',
  'defense': '#2563EB',
  'aerospace': '#4F46E5',
  'chemical': '#F59E0B',
  'automotive': '#EC4899',
  'media': '#8B5CF6',
  'utilities': '#06B6D4',
  'textile': '#84CC16',
  'entertainment': '#F97316',
  'real estate': '#0EA5E9',
  'agriculture': '#22C55E',
  'construction': '#F59E0B',
  'banking': '#1E3A8A',
  'telecommunications': '#0EA5E9',
  'healthcare': '#EF4444',
  'information technology': '#6366F1',
  'manufacturing': '#6B7280',
  'retail': '#F472B6',
  'energy': '#FACC15',
  'education': '#14B8A6',
  'consumer goods': '#FB923C',
  'electronics': '#7C3AED',
  'food and beverage': '#EA580C',
  'hospitality': '#D97706',
  'insurance': '#2563EB',
  'mining': '#92400E',
  'transportation': '#0284C7',
  'professional services': '#4B5563',
  'sports & physical activity': '#DC2626'
};
function CapabilityMapsManagement() {
  const [search, setSearch] = useState("");
  const [industryFilter, setIndustryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMap, setSelectedMap] = useState(null);
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); 
  const limit = 12;
  const apiIndustryFilter = industryFilter !== 'all' ? industryFilter : undefined;  
  const includeInactive = statusFilter !== 'all' ? true : false;

  const { 
    data: response, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useFetchModelCapabilityMaps(
    apiIndustryFilter, 
    currentPage, 
    limit, 
    includeInactive,
    search
   );
  const mapsData = response?.data || [];
  const pagination = response?.pagination || { total: 0, page: 1, limit: 12, pages: 1 };
  const { data: industriesData } = useFetchIndustries();
  const deleteMutation = useDeleteModelCapabilityMap();
  const toggleVisibilityMutation = useToggleCapabilityMapVisibility();
  const enrichedMapsData = useMemo(() => {
    return mapsData.map(map => {
      const phasesCount = map.phases?.length || 0;
      const itemsCount = map.phases?.reduce((acc, phase) => acc + (phase.items?.length || 0), 0) || 0;          
      return {
        ...map,
        phasesCount,
        itemsCount,
        color: map.color || industryColors[map?.industry?.toLowerCase()] || '#10B981',
        lastUpdatedBy: map.lastUpdateUser || 'System'
      };
    });
  }, [mapsData]);
  const filteredMaps = useMemo(() => {
    return enrichedMapsData.filter(map => {
      const matchesSearch = search === "" || 
        map.name?.toLowerCase().includes(search.toLowerCase()) ||
        (map.arabicName && map.arabicName.toLowerCase().includes(search.toLowerCase())) ||
        map.industry?.toLowerCase().includes(search.toLowerCase()) ||
        (map.description && map.description.toLowerCase().includes(search.toLowerCase()));
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "visible" && map.visible && !map.revoked) ||
        (statusFilter === "hidden" && !map.visible && !map.revoked) ||
        (statusFilter === "revoked" && map.revoked);
      
      return matchesSearch && matchesStatus;
    });
  }, [enrichedMapsData, search, statusFilter]);
  const uniqueIndustries = useMemo(() => {
    if (!industriesData) return [];
    return industriesData
      .map(industry => industry) 
      .filter(Boolean)
      .sort();
  }, [industriesData]);  
  const industryCounts = useMemo(() => {
    return uniqueIndustries.reduce((acc, industry) => {
      acc[industry] = mapsData.filter(m => m.industry === industry).length;
      return acc;
    }, {});
  }, [uniqueIndustries, mapsData]);
  const handleAddNew = () => {
    setModalMode("add");
    setSelectedMap(null);
    setIsAddEditModalOpen(true);
  };

  const handleEdit = (map) => {
    setModalMode("edit");
    setSelectedMap(map);
    setIsAddEditModalOpen(true);
  };

  const handleView = (map) => {
    setSelectedMap(map);
    setIsViewModalOpen(true);
  };

  const handleDelete = (map) => {
    setSelectedMap(map);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedMap) {
      await deleteMutation.mutateAsync(selectedMap.id);
      setIsDeleteModalOpen(false);
      setSelectedMap(null);
    }
  };

  const handleToggleVisibility = async (mapId) => {
    await toggleVisibilityMutation.mutateAsync(mapId);
  };

  const handleSaveSuccess = () => {
    refetch();
  };
  useEffect(() => {
    setCurrentPage(1);
  }, [industryFilter, statusFilter, search]);
  return (
    <>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl text-slate-800 font-bold">Capability Maps Management</h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage and configure capability maps for different industries
          </p>
        </div>
        <button
          className="btn bg-indigo-500 hover:bg-indigo-600 text-white"
          onClick={handleAddNew}
          disabled={isLoading}
        >
          <svg className="w-4 h-4 fill-current opacity-50 shrink-0" viewBox="0 0 16 16">
            <path d="M15 7H9V1c0-.6-.4-1-1-1S7 .4 7 1v6H1c-.6 0-1 .4-1 1s.4 1 1 1h6v6c0 .6.4 1 1 1s1-.4 1-1V9h6c.6 0 1-.4 1-1s-.4-1-1-1z" />
          </svg>
          <span className="hidden xs:block ml-2">Add New Map</span>
        </button>
      </div>
      <div className="flex justify-end mb-8">
        <div className="grid grid-flow-col sm:auto-cols-max justify-end sm:justify-end gap-2">
          <FilterButtonCapabilityMaps
            setSearch={setSearch}
            search={search}
            industryFilter={industryFilter}
            setIndustryFilter={setIndustryFilter}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            industries={uniqueIndustries}
            industryCounts={industryCounts}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6">
        {isLoading ? (
          <div className="col-span-12 flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          </div>
        ) : isError ? (
          <div className="col-span-12 text-center py-12">
            <div className="text-red-500 text-lg">Error loading capability maps</div>
            <p className="text-slate-500 mt-2">{error?.message}</p>
            <button
              onClick={() => refetch()}
              className="mt-4 btn bg-indigo-500 hover:bg-indigo-600 text-white"
            >
              Try Again
            </button>
          </div>
        ) : filteredMaps.length > 0 ? (
          filteredMaps.map((map) => (
            <div key={map.id} className="col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3">
              <CapabilityMapsCard
                map={map}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onView={handleView}
                onToggleVisibility={handleToggleVisibility}
              />
            </div>
          ))
        ) : (
          <div className="col-span-12 text-center py-12">
            <div className="text-slate-400 text-lg">No capability maps found</div>
            <button
              onClick={() => {
                setSearch("");
                setIndustryFilter("all");
                setStatusFilter("all");
              }}
              className="mt-4 text-indigo-500 hover:text-indigo-600"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
      {pagination.total > limit && (
        <div className="flex justify-end mt-6">
          <div className="flex items-center space-x-2">
            <button
              className="btn border-slate-200 hover:border-slate-300"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || isLoading}
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">
              Page {currentPage} of {pagination.pages} ({pagination.total} items)
            </span>
            <button
              className="btn border-slate-200 hover:border-slate-300"
              onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
              disabled={currentPage === pagination.pages || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      )}
      <AddEditCapabilityMapModal
        isOpen={isAddEditModalOpen}
        onClose={() => setIsAddEditModalOpen(false)}
        mode={modalMode}
        mapData={selectedMap}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        mapName={selectedMap?.name}
        mapArabicName={selectedMap?.arabicName}
        isLoading={deleteMutation.isLoading}
      />

      <ViewCapabilityMapModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        map={selectedMap}
      />
    </>
  );
}

export default CapabilityMapsManagement;